import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { TeamHistoryDailyDto } from '@internal/scorecards-common';
import { useLevels } from '../../hooks/useLevels';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatXTick = (date: string): string => {
  const parts = date.split('-');
  const month = Number(parts[1]) - 1;
  const day = Number(parts[2]);
  return `${day} ${MONTHS[month] ?? ''}`;
};

interface HistoryChartProps {
  daily: TeamHistoryDailyDto[];
}

export const HistoryChart = ({ daily }: HistoryChartProps) => {
  const { levels } = useLevels();

  // Flatten levels record + others into top-level keys for Recharts dataKey access.
  const chartData = useMemo(
    () =>
      daily.map(d => ({
        date: d.date,
        noRun: d.noRun,
        ...d.levels,
        __others: d.others,
      })),
    [daily],
  );

  // Level name lookup by ID
  const levelNameById = useMemo(
    () => new Map(levels.map(lv => [lv.id, lv.name])),
    [levels],
  );

  // Only render bars for levels that actually appear in the data
  const activeLevelIds = useMemo(
    () =>
      [...levels]
        .sort((a, b) => a.id.localeCompare(b.id))
        .filter(lv => daily.some(d => (d.levels[lv.id] ?? 0) > 0))
        .map(lv => lv.id),
    [levels, daily],
  );

  const hasOthers = useMemo(() => daily.some(d => d.others > 0), [daily]);

  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart
        data={chartData}
        margin={{ top: 4, right: 4, bottom: 4, left: 0 }}
      >
        <XAxis
          dataKey="date"
          tickFormatter={formatXTick}
          tick={{ fontSize: 11 }}
          interval="preserveStartEnd"
        />
        <YAxis allowDecimals={false} width={32} tick={{ fontSize: 11 }} />
        <Tooltip
          formatter={(value: number, name: string) => {
            if (name === '__others') return [value, 'Others'];
            return [value, levelNameById.get(name) ?? name];
          }}
          labelFormatter={(label: string) => label}
        />
        {activeLevelIds.map(id => (
          <Bar
            key={id}
            dataKey={id}
            stackId="a"
            fill={`var(--zf-sc-level-${id})`}
          />
        ))}
        {hasOthers && (
          <Bar
            key="__others"
            dataKey="__others"
            stackId="a"
            fill="var(--zf-sc-level-others)"
          />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
};
